import {
  ExternalExportStatus,
  getV1CompaniesCompanyIdTicketsId,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import connection from '~services/connectionModule';

import { Ticket } from './models';

const REFRESH_INTERVAL_IN_MS = 2000;
const MAX_REFRESHES = 5;

interface ExportBaseline {
  reason: Ticket['externalExportReason'];
  status: Ticket['externalExportStatus'];
  ticketId: string;
}

interface UseTicketExportRefreshProps {
  companyId: string;
  currentTicketId: string | undefined;
  onTicketRefreshed: (ticket: Ticket) => void;
}

export default function useTicketExportRefresh({
  companyId,
  currentTicketId,
  onTicketRefreshed,
}: UseTicketExportRefreshProps) {
  const [baseline, setBaseline] = useState<ExportBaseline>();
  const onTicketRefreshedRef = useRef(onTicketRefreshed);

  useEffect(() => {
    onTicketRefreshedRef.current = onTicketRefreshed;
  }, [onTicketRefreshed]);

  useEffect(() => {
    setBaseline((current) =>
      current?.ticketId === currentTicketId ? current : undefined,
    );
  }, [currentTicketId]);

  useEffect(() => {
    if (!baseline) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    let timeout: ReturnType<typeof setTimeout>;
    let lastDeliveredStatus = baseline.status;
    let lastDeliveredReason = baseline.reason;

    const poll = (remaining: number) => {
      timeout = setTimeout(async () => {
        try {
          const response = await getV1CompaniesCompanyIdTicketsId({
            path: {
              'company-id': companyId,
              id: baseline.ticketId,
            },
            signal,
          });
          const ticket = Ticket.parse(response.data.data);

          if (signal.aborted) {
            return;
          }

          const hasExportChanged =
            ticket.externalExportStatus !== lastDeliveredStatus ||
            ticket.externalExportReason !== lastDeliveredReason;

          if (hasExportChanged) {
            lastDeliveredStatus = ticket.externalExportStatus;
            lastDeliveredReason = ticket.externalExportReason;
            onTicketRefreshedRef.current(ticket);
          }

          const hasSettledOnNewValue =
            ticket.externalExportStatus !== ExternalExportStatus.PENDING &&
            (ticket.externalExportStatus !== baseline.status ||
              ticket.externalExportReason !== baseline.reason);

          if (!hasSettledOnNewValue && remaining > 1) {
            poll(remaining - 1);
          }
        } catch (error) {
          if (signal.aborted) {
            return;
          }

          if (remaining > 1) {
            poll(remaining - 1);

            return;
          }

          try {
            connection.handleRequestError(
              error,
              t('error_messages.tickets.failed_to_refresh_export_status'),
            );
          } catch {
            // Error already handled (toast + Sentry)
          }
        }
      }, REFRESH_INTERVAL_IN_MS);
    };

    poll(MAX_REFRESHES);

    return () => {
      abortController.abort();
      clearTimeout(timeout);
    };
  }, [baseline, companyId]);

  return useCallback((savedTicket: Ticket) => {
    setBaseline(
      savedTicket.vetaCenterBound
        ? {
            reason: savedTicket.externalExportReason,
            status: savedTicket.externalExportStatus,
            ticketId: savedTicket.id,
          }
        : undefined,
    );
  }, []);
}
